import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { useSession } from "src/auth/useSession";

/** Must match ORGANIZATION_LINK_PARAM in the API's notificationService (emailed links carry it). */
export const ORGANIZATION_LINK_PARAM = "org";

/**
 * A link that names its organization (`?org=<id>`, as notification emails do) opens in that
 * organization: when another one is active, the session switches first (if the API lets the person
 * in), then the parameter is dropped. Returns true while that switch is pending, so the shell can
 * hold the page instead of loading it in the wrong organization.
 */
export const useOrganizationLink = (): boolean => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { user, activeOrganization, switchOrganization } = useSession();
	const handled = useRef<string | null>(null);
	const requested = searchParams.get(ORGANIZATION_LINK_PARAM);
	// Not checked against the person's own organizations: a global admin enters ones they are not a
	// member of. The API decides, and a refused switch leaves the active organization as it was.
	const needsSwitch = Boolean(user && requested && requested !== activeOrganization?.id);

	useEffect(() => {
		if (!user || !requested || handled.current === requested) return;
		handled.current = requested;
		const dropParam = () =>
			setSearchParams(
				params => {
					params.delete(ORGANIZATION_LINK_PARAM);
					return params;
				},
				{ replace: true }
			);
		if (!needsSwitch) {
			dropParam();
			return;
		}
		// A failed switch leaves the active organization; the page then says what it says there.
		void switchOrganization(requested)
			.catch(() => undefined)
			.finally(dropParam);
	}, [needsSwitch, requested, setSearchParams, switchOrganization, user]);

	return needsSwitch;
};
